import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { Element } from '../../../../common/html/element'
import { Notification } from '../../../../common/notification'
import { SCREEN } from '../../../../common/selectCurrencyUnit/constant'
import { useFormatCurrency } from '../../../../common/selectCurrencyUnit/useFormatCurrency'
import EventEmitter, {
  DID_MOUNT_COMPONENT,
} from '../../../../helpers/EventEmitter'
import { keyBy } from '../../../../helpers/common'
import { formatByTheme } from '../../../../helpers/theme'
import {
  formatVal,
  isInValidValue,
  valToPercent,
} from '../../../../helpers/value'
import { COMPANY_TYPES } from '../../../../utils/common'
import {
  typeComponentBank,
  typeComponentCorporate,
  typeComponentLifeInsurance,
  typeComponentNonBank,
  typeComponentNonLifeInsurance,
  typeComponentSecurity,
} from '../constant'
import FinancialAnalysisService from '../core/service'
import classes from '../index.module.scss'
import getDetailPanel from './detailPanelHelpers'
import getDataThunk from './getDataThunkHelpers'
import getPanelDownload from './panelDownloadHelpers'
import renderComponent from './renderComponentHelpers'
import resetAllStore from './resetStoreHelpers'
import getSettingPanel from './settingPanelHelpers'

export const getKeyPanels = (companyType) => {
  return Object.values(
    companyType === COMPANY_TYPES.CORPORATE
      ? typeComponentCorporate
      : companyType === COMPANY_TYPES.BANK
      ? typeComponentBank
      : companyType === COMPANY_TYPES.NON_BANK
      ? typeComponentNonBank
      : companyType === COMPANY_TYPES.LIFE_INSURANCE
      ? typeComponentLifeInsurance
      : companyType === COMPANY_TYPES.NON_LIFE_INSURANCE
      ? typeComponentNonLifeInsurance
      : typeComponentSecurity,
  )
}

export const getStatusQuickAssessment = (type, showName = true) => {
  let content
  switch (type) {
    case 'Concerned':
      content = (
        <>
          <Element
            style={{ background: '#E0505B' }}
            className={classes.contentStatus}
          />
          {showName && <Translate value="financialAnalysis.CONCERNED" />}
        </>
      )
      break
    case 'Good':
      content = (
        <>
          <Element
            style={{ background: '#3DA967' }}
            className={classes.contentStatus}
          />
          {showName && <Translate value="financialAnalysis.GOOD" />}
        </>
      )
      break
    case 'Neutral':
      content = (
        <>
          <Element
            style={{ background: '#FFCD60' }}
            className={classes.contentStatus}
          />
          {showName && <Translate value="financialAnalysis.NEUTRAL" />}
        </>
      )
      break
    default:
      content = <></>
      break
  }

  return <div className="dFlex alignItemsCenter">{content}</div>
}

const mapData = (data, isEachYear) => {
  const dataByYear = {}
  const count = {}

  data.forEach((item) => {
    item.values?.forEach(({ realYear, value }) => {
      const keyCount = item.ratioCode + realYear

      if (value !== undefined || !isEachYear) {
        if (!dataByYear[realYear]) {
          dataByYear[realYear] = !isEachYear ? { year: realYear } : {}
        }

        dataByYear[realYear][item.ratioCode + (count[keyCount] || '')] =
          !isInValidValue(value)
            ? value * (item.oppositeChartValue ? -1 : 1)
            : null
      }

      count[keyCount] = !count[keyCount] ? 1 : count[keyCount] + 1
    })
  })

  return dataByYear
}

const convertDataViewByTrend = (data = []) => {
  const dataTrend = data.filter((item) => item.viewByTrend)

  if (!dataTrend.length) {
    return []
  }

  if (
    dataTrend.every(({ values }) =>
      values.every(({ value }) => isInValidValue(value)),
    )
  ) {
    return []
  }

  const dataByYear = mapData(dataTrend)

  return Object.values(dataByYear)
}

const convertDataViewByEachYear = (
  data = [],
  disableFilterEachYear = false,
) => {
  const dataEachYear = data.filter(
    (item) => disableFilterEachYear || item.viewByEachYear,
  )

  if (!dataEachYear.length) {
    return {}
  }

  const dataByYear = mapData(dataEachYear, true)

  Object.keys(dataByYear).forEach((year) => {
    const dataYear = []

    Object.keys(dataByYear[year]).forEach((id) => {
      dataYear.push({ id, value: dataByYear[year][id] })
    })

    dataByYear[year] = dataYear
  })

  return dataByYear
}

const convertDataViewByTable = (data = []) => {
  const count = {}
  const childIds = {}

  const dataTable = data
    .filter((item) => item.viewByTable)
    .map((item) => {
      const id = item.ratioCode + (count[item.ratioCode] || '')

      if (item.parentCode && item.parentCode !== item.ratioCode) {
        if (!childIds[item.parentCode]) {
          childIds[item.parentCode] = [id]
        } else {
          childIds[item.parentCode].push(id)
        }
      }

      const newItem = {
        id,
        ratioCode: item.ratioCode,
        parentCode: item.parentCode,
        ratioName: item.ratioName,
        unit: item.unit,
        exchangeRateType: item.exchangeRateType,
      }

      item.values.forEach(({ realYear, value }) => {
        newItem[realYear] = value
      })

      count[item.ratioCode] = !count[item.ratioCode]
        ? 1
        : count[item.ratioCode] + 1

      return newItem
    })
    .map((item) => ({
      ...item,
      childIds: childIds[item.ratioCode],
    }))

  const byIdTable = keyBy(dataTable, 'id')

  const idTable = Object.keys(byIdTable).filter((id) => {
    const data = byIdTable[id]
    if (!data.parentCode) return true
    return data.ratioCode === data.parentCode
  })

  return {
    idTable,
    byIdTable,
    years:
      data[0]?.values &&
      data.some((indicator) => {
        if (indicator.values?.length) {
          return indicator.values.some(({ value }) => value !== undefined)
        }
        return false
      })
        ? data[0].values.map((item) => item.realYear)
        : [],
  }
}

const convertDataIndicator = (data = []) => {
  if (!data.length) {
    return {
      indicator: [],
      indicatorById: {},
    }
  }

  const indicatorById = {}
  const indicator = []
  const count = {}

  data.forEach((item) => {
    const keyCount = item.ratioCode

    indicatorById[item.ratioCode + (count[keyCount] || '')] = {
      ratioName: item.ratioName,
      exchangeRateType: item.unit === 'VND' ? item.exchangeRateType : undefined,
    }

    indicator.push({
      id: item.ratioCode + (count[keyCount] || ''),
      name: item.ratioName,
    })

    count[keyCount] = !count[keyCount] ? 1 : count[keyCount] + 1
  })

  return { indicatorById, indicator }
}

export const convertDataApi = (
  data = [],
  isViewByTrend = true,
  isViewByEachYear = true,
  isViewByTable = true,
  disableFilterEachYear = false,
) => {
  let result = {}

  if (isViewByTrend) {
    result.dataViewByTrend = convertDataViewByTrend(data)
  }

  if (isViewByEachYear) {
    result.dataViewByEachYear = convertDataViewByEachYear(
      data,
      disableFilterEachYear,
    )
  }

  if (isViewByTable) {
    result = { ...result, ...convertDataViewByTable(data) }
  }

  result = { ...result, ...convertDataIndicator(data) }

  return result
}

export const convertDataHighlight = (data = []) => {
  if (!data.length) {
    return {}
  }

  const dataById = keyBy(data, 'ratioCode')

  const result = mapData(data)

  Object.keys(result).forEach((year) => {
    const dataYear = []

    Object.keys(result[year]).forEach((id) => {
      const detail = dataById[id]
      if (detail) {
        dataYear.push({
          id,
          value: result[year][id],
          ratioName: detail?.ratioName,
          unit: detail?.unit,
        })
      }
    })

    result[year] = dataYear
  })

  return result
}

export const mapAllVNDValue = (data = []) => {
  return data
    .filter((item) => item.unit === 'VND')
    .map((item) => {
      const exchangeRateType = item.exchangeRateType
      return item.values.map((i) => ({
        value: i.value,
        year: i.realYear,
        exchangeRateType,
      }))
    })
    .flat()
}

export const sortKeySchemaTable = (keys) => {
  return keys.sort((key1, key2) => {
    if (isNaN(key1)) {
      return -1
    }

    if (isNaN(key2)) {
      return 1
    }

    return 1
  })
}

const Td = ({ value, rowId, colId, selectByIdTable, selectIndicatorById }) => {
  const locale = useSelector((state) => state.i18n.locale)
  const byIdTable = useSelector(selectByIdTable)
  const indicatorById = useSelector(selectIndicatorById)

  const { formatedUnit, formatCurrency } = useFormatCurrency(
    SCREEN.FINANCIAL_ANALYSIS,
  )

  const { childIds, unit } = byIdTable[rowId]

  const renderValue = (value, unit) => {
    return colId === 'unit' && unit === 'VND'
      ? formatedUnit
      : ['ratioName', 'unit'].includes(colId)
      ? value
      : unit === 'VND'
      ? formatCurrency({
          value,
          year: Number(colId),
          exchangeRateType: indicatorById[rowId]?.exchangeRateType,
        })
      : unit[0] === '%'
      ? valToPercent(value, locale, false, false, 2, true)
      : formatVal(value, locale)
  }

  const renderChilds = () => {
    if (colId === 'ratioName' || !childIds || !childIds.length) return null
    return childIds.map((id) => (
      <div key={id} className="mt1">
        {renderValue(byIdTable[id][colId], byIdTable[id].unit)}
      </div>
    ))
  }

  return (
    <>
      {renderValue(value, unit)}
      {renderChilds()}
    </>
  )
}

export const renderTd =
  (selectByIdTable, selectIndicatorById) =>
  (value, { rowId, colId }) =>
    (
      <Td
        selectByIdTable={selectByIdTable}
        selectIndicatorById={selectIndicatorById}
        value={value}
        rowId={rowId}
        colId={colId}
      />
    )

export const getValueAndTitleCheckbox = (indicatorById, value) => ({
  value,
  title: indicatorById[value]?.ratioName,
})

export const saveTemplate = async (data, companyType) => {
  try {
    if (data.templateId) {
      const res = await FinancialAnalysisService.updateTemplate({
        financialAnalysisTemplateName: data.templateName,
        financialAnalysisTemplateData: JSON.stringify(data.panels),
        financialAnalysisTemplateId: data.templateId,
        templateType: data.companyType || companyType,
      })
      return res.success
    } else {
      const res = await FinancialAnalysisService.saveTemplate({
        financialAnalysisTemplateName: data.templateName,
        financialAnalysisTemplateData: JSON.stringify(data.panels),
        templateType: data.companyType || companyType,
      })
      return res.success
    }
  } catch {
    return false
  }
}

export const getYearTabs = (years) => {
  return years.map((year) => {
    return {
      key: String(year),
      label: String(year),
    }
  })
}

export const getNoDataComponent = (
  isDynamicReport = false,
  isTable = false,
) => {
  if (isDynamicReport) {
    setTimeout(() => EventEmitter.dispatch(DID_MOUNT_COMPONENT), 500)
  }

  return !isDynamicReport ? (
    <div className={['h100 pt2 pb2', classes.noDataRoaRoe].join(' ')}>
      <Notification
        isI18n
        value="common.NO_DATA"
        isDynamicReport={isDynamicReport}
      />
    </div>
  ) : (
    <Element
      style={{
        color: '#9C9C9C',
        fontSize: 12,
        paddingTop: isTable ? 8 : 0,
        paddingLeft: isTable ? 28 : 16,
      }}
    >
      <Translate value="common.NO_DATA" />
    </Element>
  )
}

export const getStyleHeightContainer = (
  isDynamicReport = false,
  isNoData = false,
  height,
) => {
  if (!isDynamicReport) {
    return '100%'
  }

  if (isNoData) {
    return 'unset'
  }

  return height
}

export const handleIconDownload = (id, isDisable = false) => {
  if (id) {
    const icon = document.getElementById(id)?.querySelector('.icon-download')
    if (icon) {
      icon.style.pointerEvents = isDisable ? 'none' : 'all'
      icon.style.color = isDisable
        ? formatByTheme('#9C9C9C')
        : formatByTheme('#0553A5')
    }
  }
}

export const reOrderDataByYear = (data, yearKey) => {
  return data?.sort((a, b) => {
    if (a[yearKey] < b[yearKey]) return -1
    if (a[yearKey] > b[yearKey]) return 1
    return 0
  })
}

// Nếu tất cả giá trị của trường value trong mảng values là undefined sẽ loại bỏ
export const filterItemsWithAllValuesDefined = (arr) => {
  return arr.filter((item) =>
    item.values.some((item1) => item1.value !== undefined),
  )
}

export {
  getDataThunk,
  getDetailPanel,
  getPanelDownload,
  getSettingPanel,
  renderComponent,
  resetAllStore,
}
